import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import { DateRange } from '@material-ui/pickers';

import { JobStatusEvent, subscribe } from '../../../services/centrifugeService';

import { triggerManualCalculation } from '../IBDashboardService';

import { ClientFilterType } from '../IBDashboardModels';

import { useAppData } from '../../../providers';

type JobStatus = 'idle' | 'running';

interface UseCommissionJobProps {
  getValues: (code: string, dateRange: DateRange<Date>, clientFilterType: ClientFilterType, silent?: boolean) => Promise<void>;

  selectedPartnership: string;
  selectedDateRange: DateRange<Date>;
  selectedClientFilterType: ClientFilterType;
  onRefreshCompleted?: () => void;
}

interface UseCommissionJobResult {
  jobStatus: JobStatus;
  lastJobDate: string;
  handleRefresh: () => Promise<void>;
}

const getTokenFromStorage = (): string => {
  const storageString = sessionStorage.getItem('__STATE_MACHINE__');

  if (!storageString) {
    return '';
  }

  try {
    const storage = JSON.parse(storageString);

    return storage && storage.token ? storage.token : '';
  } catch {
    return '';
  }
};

export const useCommissionJob = ({ getValues, selectedPartnership, selectedDateRange, selectedClientFilterType, onRefreshCompleted }: UseCommissionJobProps): UseCommissionJobResult => {
  const [jobStatus, setJobStatus] = useState<JobStatus>('idle');

  const [lastJobDate, setLastJobDate] = useState<string>('');

  const { enqueueSnackbar } = useSnackbar();

  const { userData, authToken } = useAppData();

  const handlerRef = useRef<((data: JobStatusEvent) => void) | null>(null);

  const unsubscribeRef = useRef<(() => void) | null>(null);

  const getValuesRef = useRef(getValues);

  const selectedPartnershipRef = useRef(selectedPartnership);

  const selectedDateRangeRef = useRef(selectedDateRange);

  const selectedClientFilterTypeRef = useRef(selectedClientFilterType);

  const onRefreshCompletedRef = useRef(onRefreshCompleted);

  const enqueueSnackbarRef = useRef(enqueueSnackbar);

  const jobStatusRef = useRef<JobStatus>(jobStatus);

  useEffect(() => {
    getValuesRef.current = getValues;
  }, [getValues]);

  useEffect(() => {
    selectedPartnershipRef.current = selectedPartnership;
  }, [selectedPartnership]);

  useEffect(() => {
    selectedDateRangeRef.current = selectedDateRange;
  }, [selectedDateRange]);

  useEffect(() => {
    selectedClientFilterTypeRef.current = selectedClientFilterType;
  }, [selectedClientFilterType]);

  useEffect(() => {
    onRefreshCompletedRef.current = onRefreshCompleted;
  }, [onRefreshCompleted]);

  useEffect(() => {
    enqueueSnackbarRef.current = enqueueSnackbar;
  }, [enqueueSnackbar]);

  useEffect(() => {
    jobStatusRef.current = jobStatus;
  }, [jobStatus]);

  const userId = useMemo(() => {
    if (userData) {
      try {
        const user = JSON.parse(userData);

        const id = user && (user.userID || user.userId || user.id || (user.accounts && user.accounts[0] && (user.accounts[0].userID || user.accounts[0].userId || user.accounts[0].id)));

        if (id) {
          return String(id);
        }
      } catch {
        return '';
      }
    }

    return sessionStorage.getItem('commissionDashboardUserId') || '';
  }, [userData]);

  const token = useMemo(() => authToken || getTokenFromStorage(), [authToken]);

  const channel = useMemo(() => {
    if (!userId) {
      return '';
    }

    return `commissions:dashboard:${userId}`;
  }, [userId]);

  useEffect(() => {
    handlerRef.current = async (data: JobStatusEvent) => {
      switch (data.type) {
        case 'CommissionCalculationStarted':
          jobStatusRef.current = 'running';

          setJobStatus('running');
          break;

        case 'CommissionCalculationEnded':
          try {
            if (data.date) {
              setLastJobDate(data.date);
            }

            await getValuesRef.current(selectedPartnershipRef.current, selectedDateRangeRef.current, selectedClientFilterTypeRef.current, true);

            if (onRefreshCompletedRef.current) {
              onRefreshCompletedRef.current();
            }
          } finally {
            jobStatusRef.current = 'idle';

            setJobStatus('idle');
          }

          break;

        case 'CommissionCalculationFailed':
          jobStatusRef.current = 'idle';

          setJobStatus('idle');

          enqueueSnackbarRef.current(data.message || 'Commission calculation failed', {
            variant: 'error',
          });

          break;

        default:
          break;
      }
    };

    return () => {
      handlerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!channel || !token) {
      return () => {};
    }

    unsubscribeRef.current = subscribe(channel, token, (data) => {
      if (handlerRef.current) {
        handlerRef.current(data);
      }
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }

      unsubscribeRef.current = null;
    };
  }, [channel, token]);

  const handleRefresh = useCallback(async () => {
    if (jobStatusRef.current === 'running') {
      return;
    }

    try {
      jobStatusRef.current = 'running';

      setJobStatus('running');

      await triggerManualCalculation();
    } catch (error: any) {
      jobStatusRef.current = 'idle';

      setJobStatus('idle');

      if (error && error.response && error.response.status === 409) {
        enqueueSnackbarRef.current('Calculation already in progress', {
          variant: 'warning',
        });

        return;
      }

      enqueueSnackbarRef.current('Failed to trigger calculation', {
        variant: 'error',
      });
    }
  }, []);

  return {
    jobStatus,
    lastJobDate,
    handleRefresh,
  };
};
